/* eslint-disable max-lines */
import {
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Box,
  createFilterOptions,
  Autocomplete,
} from '@mui/material';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import { FundingScreenStyles as classes } from './FundingScreenStyles';
import { CustomInput } from '../CustomInput/CustomInput';
import { CustomSelect } from '../CustomSelect/CustomSelect';
import { dgiMapping } from '../../../services/dgiMapping';
import { dgiContMapping } from '../../../services/dgiContMapping';
import { commonUtility, commonUtilityValueReturn } from '../UtilityFunctions';
import {
  ACCOUNT_NUM,
  ACCOUNT_NUMBER,
  ACCOUNT_TYPE,
  DEPOSIT_AMOUNT,
  DEPOSIT_MAX_AMOUNT_ERROR,
  DGI_CONT_ACC_NUM,
  EN,
  ENTER_ACCOUNT_NUMBER,
  INVALID_FORMAT_ERR,
  MANDATORY_FIELD_ERR,
  MAX_DEPOSIT_ERR,
  MINIMUM_DEPOSIT,
  MINIMUM_DEPOSIT_AMOUNT,
  MIN_DEPOSIT_ERR,
  REQUIRED,
  SOURCE_OF_FUNDS,
  SOURCE_OF_FUNDS_PLACEHOLDER,
  TAX_YEAR,
  TYPE_OF_ACCOUNT,
} from '../../../constants';
import {
  changeDgiAccNumberAndroid,
  isBundle,
  isCDIraproduct,
  isCDproduct,
  isPlaidProduct,
  nbrToCurrencyFormat,
} from '../../commonCode';
import { productMapping } from '../../../services/productMapping';
import { CustomSelectAccountNo } from '../CustomSelect/CustomSelectAccountNo';
import { CommonStyles } from '../CommonStyles';
import { DGIProductUIStyles as styledClasses } from '../../DGI/DGIProductUIStyles';
import { SourceOfFundsList } from '../CertificateOfDeposit/SourceOfFundsList';
import { FundingScreenCards } from '../CertificateOfDeposit/FundingScreenCards';
import { DownArrowIcon } from '../../../images/svg/CustomizedSVG';

const OrientalAccount = ({ setIsSubmitClicked, isSubmitClicked }) => {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const {
    fundData,
    fundDataErrors,
    setFundData,
    setFundDataErrors,
    setIsEdeliveryScreenDirty,
    setIsFundingScreenDirty,
    customerType,
    accNoListForOrientalAccount,
    setAccNoListForOrientalAccount,
    productName,
    setSelectedOrientalAccountProgress,
    isBalanceValidationFail,
    savedDataFlow,
    language,
    cdFlowOffers,
    theme,
    productDataIRA,
    flowTypeETB,
  } = leadGenerationContext;
  const commonClass = CommonStyles(leadGenerationContext?.theme);

  useEffect(() => {
    if (savedDataFlow && fundData.orientalAccount && customerType === 'ETB') {
      const matchingItem = accNoListForOrientalAccount.find((item) => item.Id === fundData.Id);
      setSelectedOrientalAccountProgress(matchingItem);
    }
  }, [accNoListForOrientalAccount, fundData.Id]);
  useEffect(() => {
    if (cdFlowOffers?.depositAmount) {
      if (isCDproduct(productName)) {
        setFundData({
          ...fundData,
          depositAmount: cdFlowOffers?.depositAmount,
          minDepositAmount: '1000',
        });
      } else if (isCDIraproduct(productName)) {
        setFundData({
          ...fundData,
          depositAmount: productDataIRA?.depositAmount
            ? productDataIRA?.depositAmount
            : cdFlowOffers?.depositAmount,
          minDepositAmount: '250',
        });
        setIsFundingScreenDirty(true);
      }
    }
  }, []);

  const maxAmountCheck = (e) => {
    if (!dgiMapping(leadGenerationContext.productName)) {
      setFundData((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.value,
      }));
      setFundDataErrors((prevState) => ({
        ...prevState,
        [e.target.name]: DEPOSIT_MAX_AMOUNT_ERROR,
      }));
    }
    if (dgiMapping(leadGenerationContext.productName)) {
      setFundData((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.value,
      }));
    }
  };

  const getAmountByAccountNumberBalance = (selectedAccId) => {
    const selectedAccount = accNoListForOrientalAccount?.find(
      (account) => account.Id === selectedAccId
    );
    return selectedAccount?.Balance || null;
  };
  const getAmountByAccountNumberBalanceEmpty = (selectedAccountId) => {
    const selectedAccNo = accNoListForOrientalAccount?.find(
      (account) => account.Id === selectedAccountId
    );
    let isEmpty;
    if (selectedAccNo?.Balance === '') {
      isEmpty = true;
    } else {
      isEmpty = false;
    }
    return isEmpty;
  };
  const depositAmountChange = (e) => {
    if (
      accNoListForOrientalAccount?.length > 0 &&
      customerType === 'ETB' &&
      isPlaidProduct(productName)
    ) {
      const updateAccountDetails = accNoListForOrientalAccount?.map((option) => {
        const val = option.Balance.replace(/,/g, '');
        const balance = parseFloat(val);
        let isDepositeGreaterThanBalanceSingle = false;
        if (fundData?.depositAmount && option.Balance != '') {
          isDepositeGreaterThanBalanceSingle = balance < e.target.value;
        }
        return { ...option, isDepositeGreaterThanBalanceSingle };
      });
      setAccNoListForOrientalAccount(updateAccountDetails);
    }
    if (e.target.value?.match(/^\d*$/) && e.target.value <= 1000000) {
      setFundData({ ...fundData, [e.target.name]: e.target.value });
      if (dgiMapping(leadGenerationContext.productName) || isPlaidProduct(productName)) {
        if (
          parseInt(e.target.value, 10) >= parseInt(fundData?.minDepositAmount, 10) &&
          parseInt(e.target.value, 10) <= parseInt(5000, 10)
        ) {
          if (
            fundData.accountNumber &&
            accNoListForOrientalAccount?.length > 0 &&
            customerType === 'ETB' &&
            isPlaidProduct(productName) &&
            getAmountByAccountNumberBalanceEmpty(fundData.Id) === false &&
            parseFloat(e.target.value) > parseFloat(getAmountByAccountNumberBalance(fundData.Id))
          ) {
            setFundData({ ...fundData, [e.target.name]: e.target.value });
            setFundDataErrors({
              ...fundDataErrors,
              [e.target.name]:
                '* Please review the deposit amount entered as it is more than the available balance on this account',
            });
          } else {
            setFundDataErrors({ ...fundDataErrors, [e.target.name]: '' });
          }
        } else if (parseInt(e.target.value, 10) > parseInt(5000, 10)) {
          setFundDataErrors({
            ...fundDataErrors,
            [e.target.name]:
              productMapping(leadGenerationContext.productName) === 'Libre' ||
              isPlaidProduct(productName)
                ? t('The maximum limit for initial deposit is $5,000.', {
                    defaultValue: 'The maximum limit for initial deposit is $5,000.',
                  })
                : MAX_DEPOSIT_ERR,
          });
        } else {
          setFundDataErrors({
            ...fundDataErrors,
            [e.target.name]:
              isPlaidProduct(productName) ||
              productMapping(leadGenerationContext.productName) === 'Libre'
                ? `A minimum deposit of $${fundData?.minDepositAmount} is required to open this account.`
                : MIN_DEPOSIT_ERR,
          });
        }
      } else if (parseInt(e.target.value, 10) >= parseInt(fundData?.minDepositAmount, 10)) {
        setFundDataErrors({
          ...fundDataErrors,
          [e.target.name]: '',
        });
      } else {
        setFundDataErrors({
          ...fundDataErrors,
          [e.target.name]: MIN_DEPOSIT_ERR,
        });
      }
    } else if (e.target?.value > 1000000) {
      maxAmountCheck(e);
    } else if (e.target.value === '') {
      setFundData({ ...fundData, [e.target.name]: '' });
      setFundDataErrors({
        ...fundDataErrors,
        [e.target.name]: MANDATORY_FIELD_ERR,
      });
    }
  };

  const typeAccountYearChange = (e) => {
    if (e.target.value.length > 0) {
      setFundData({ ...fundData, [e.target.name]: e.target.value });

      setFundDataErrors({
        ...fundDataErrors,
        [e.target.name]: '',
      });
    } else if (e.target.value === '') {
      setFundData({ ...fundData, [e.target.name]: '' });

      setFundDataErrors({
        ...fundDataErrors,
        [e.target.name]: MANDATORY_FIELD_ERR,
      });
    }
  };
  const typeAccountChange = (e) => {
    typeAccountYearChange(e);
  };
  const accountNumberChange = (e) => {
    if (e.target?.value?.match(/^\d*$/)) {
      if (
        // leadGenerationContext.productName === 'Progresa Account' ||
        // leadGenerationContext.productName === 'Cuenta Progresa' ||
        leadGenerationContext.productName === 'Cuenta Ahorra' ||
        leadGenerationContext.productName === 'Ahorra Account'
      ) {
        if (e.target.value.length >= 9 && e.target.value.length <= 16) {
          setFundData({ ...fundData, [e.target.name]: e.target.value });
          setFundDataErrors({
            ...fundDataErrors,
            [e.target.name]: '',
          });
          if (e.target.value.match(/^0*$/g)) {
            setFundData({ ...fundData, [e.target.name]: e.target.value });
            setFundDataErrors({
              ...fundDataErrors,
              [e.target.name]: '* Invalid Account Number',
            });
          }
        } else {
          setFundData({ ...fundData, [e.target.name]: e.target.value });

          setFundDataErrors({
            ...fundDataErrors,
            [e.target.name]: INVALID_FORMAT_ERR,
          });
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (e.target.value.length >= 9 && e.target.value.length <= 12) {
          setFundData({ ...fundData, [e.target.name]: e.target.value });
          setFundDataErrors({
            ...fundDataErrors,
            [e.target.name]: '',
          });
          if (e.target.value.match(/^0*$/g)) {
            setFundData({ ...fundData, [e.target.name]: e.target.value });
            setFundDataErrors({
              ...fundDataErrors,
              [e.target.name]: '* Invalid Account Number',
            });
          }
        } else {
          setFundData({ ...fundData, [e.target.name]: e.target.value });

          setFundDataErrors({
            ...fundDataErrors,
            [e.target.name]: INVALID_FORMAT_ERR,
          });
        }
      }
    } else if (e.target.value === '') {
      setFundData({ ...fundData, [e.target.name]: '' });
      setFundDataErrors({ ...fundDataErrors, [e.target.name]: MANDATORY_FIELD_ERR });
    }
  };
  const getAccontNoById = (e) => {
    const selectedAccount = accNoListForOrientalAccount?.find((account) => account.Id === e);
    return selectedAccount || null;
  };

  const accountNumberProgresa = (e) => {
    const conditionAccNo = isPlaidProduct(productName)
      ? getAccontNoById(e.target.value).AccountNumber.length >= 9 &&
        getAccontNoById(e.target.value).AccountNumber.length <= 17
      : getAccontNoById(e.target.value).AccountNumber.length >= 9 &&
        getAccontNoById(e.target.value).AccountNumber.length <= 16;
    if (e.target.value.length > 0) {
      if (conditionAccNo) {
        if (
          isBalanceValidationFail &&
          fundDataErrors.depositAmount ===
            '* Please review the deposit amount entered as it is more than the available balance on this account'
        ) {
          setFundDataErrors((prevState) => ({
            ...prevState,
            depositAmount: '',
            [e.target.name]: '',
          }));
          setSelectedOrientalAccountProgress(getAccontNoById(e.target.value));
          setFundData({
            ...fundData,
            [e.target.name]: getAccontNoById(e.target.value).AccountNumber,
            Id: e.target.value,
          });
        } else if (
          fundData.depositAmount &&
          accNoListForOrientalAccount?.length > 0 &&
          customerType === 'ETB' &&
          getAmountByAccountNumberBalanceEmpty(getAccontNoById(e.target.value).Id) === false &&
          fundDataErrors.depositAmount ===
            '* Please review the deposit amount entered as it is more than the available balance on this account' &&
          isPlaidProduct(productName) &&
          fundData.depositAmount <
            getAmountByAccountNumberBalance(getAccontNoById(e.target.value).Id)
        ) {
          setFundDataErrors({
            ...fundDataErrors,
            depositAmount: '',
            [e.target.name]: '',
          });
          setSelectedOrientalAccountProgress(getAccontNoById(e.target.value));
          setFundData({
            ...fundData,
            [e.target.name]: getAccontNoById(e.target.value).AccountNumber,
            Id: e.target.value,
          });
        } else {
          setSelectedOrientalAccountProgress(getAccontNoById(e.target.value));
          setFundData({
            ...fundData,
            [e.target.name]: getAccontNoById(e.target.value).AccountNumber,
            Id: e.target.value,
          });

          setFundDataErrors({
            ...fundDataErrors,
            [e.target.name]: '',
          });
        }
      } else {
        setFundData({
          ...fundData,
          [e.target.name]: getAccontNoById(e.target.value).AccountNumber,
          Id: e.target.value,
        });
        setFundDataErrors({
          ...fundDataErrors,
          [e.target.name]: INVALID_FORMAT_ERR,
        });
      }
    } else if (e.target.value === '') {
      if (
        isBalanceValidationFail &&
        fundDataErrors.depositAmount ===
          '* Please review the deposit amount entered as it is more than the available balance on this account'
      ) {
        setFundDataErrors((prevState) => ({
          ...prevState,
          depositAmount: '',
          [e.target.name]: MANDATORY_FIELD_ERR,
        }));
        setSelectedOrientalAccountProgress({});
        setFundData({ ...fundData, [e.target.name]: '', Id: '' });
      } else if (
        isPlaidProduct(productName) &&
        fundDataErrors.depositAmount ===
          '* Please review the deposit amount entered as it is more than the available balance on this account'
      ) {
        setSelectedOrientalAccountProgress({});
        setFundData({ ...fundData, [e.target.name]: '', Id: '' });

        setFundDataErrors({
          ...fundDataErrors,
          depositAmount: '',
          [e.target.name]: MANDATORY_FIELD_ERR,
        });
      } else {
        setSelectedOrientalAccountProgress({});
        setFundData({ ...fundData, [e.target.name]: '', Id: '' });

        setFundDataErrors({
          ...fundDataErrors,
          [e.target.name]: MANDATORY_FIELD_ERR,
        });
      }
    }
  };
  const taxYearChange = (e) => {
    typeAccountYearChange(e);
  };
  const handleChange = (e) => {
    if (dgiMapping(leadGenerationContext.productName)) {
      setIsFundingScreenDirty(true);
      setIsEdeliveryScreenDirty(true);
    } else {
      setIsFundingScreenDirty(true);
    }
    switch (e.target.name) {
      case 'depositAmount':
        depositAmountChange(e);
        break;
      case 'typeOfAccount':
        typeAccountChange(e);
        break;
      case 'accountNumber':
        // Plaid Acc handle change accountNumberProgresa
        if (customerType === 'ETB' && isPlaidProduct(productName)) {
          accountNumberProgresa(e);
        } else {
          accountNumberChange(e);
        }
        break;

      case 'taxYear':
        taxYearChange(e);
        break;
      default:
        break;
    }
  };

  const handleBlur = (e) => {
    const currencyValue = e?.target?.value?.replace(/^0+/, '');
    const newCurrencyValue = nbrToCurrencyFormat(currencyValue);
    setFundData((prevState) => ({
      ...prevState,
      [e?.target?.name]: newCurrencyValue,
    }));
  };

  const handleFocus = (e) => {
    const currencyValue = e.target.value.toString().replace(/[^\w\s]/gi, '');
    setFundData((prevState) => ({
      ...prevState,
      [e.target.name]: currencyValue,
    }));
  };

  const handleChangeDgiAccAndroid = (e) => {
    changeDgiAccNumberAndroid(e, leadGenerationContext);
  };

  const handleAutoCompleteFunds = (name, value) => {
    // autoCompleteCommon(name, value, leadGenerationContext);
    leadGenerationContext.setIsTrustInfoDirty(true);
    leadGenerationContext.setIsFinanceInfoDirty(true);
    leadGenerationContext.setIsFundingScreenDirty(true);
    leadGenerationContext.setIsEdeliveryScreenDirty(true);
    leadGenerationContext.setFundData((prevState) => ({
      ...prevState,
      sourceofFunds: value,
    }));
    if (!value) {
      leadGenerationContext.setFundDataErrors((prevState) => ({
        ...prevState,
        sourceofFunds: MANDATORY_FIELD_ERR,
      }));
    } else {
      leadGenerationContext.setFundDataErrors((prevState) => ({
        ...prevState,
        sourceofFunds: '',
      }));
    }
  };
  const filterOptionsEmp = createFilterOptions({
    matchFrom: 'start',
    stringify: language === EN ? (option) => option.en : (option) => option.es,
  });

  const handleAccountsView = () => (
    <Grid size={{ xs: 12 }}>
      <FundingScreenCards
        accountsResponse={accNoListForOrientalAccount}
        setIsSubmitClicked={setIsSubmitClicked}
        isSubmitClicked={isSubmitClicked}
      />
    </Grid>
  );

  const gridSizeForBundle = isBundle(productName);
  return (
    <>
      <Grid
        container
        size={{ md: 12, lg: 12 }}
        sx={[
          classes.inputs_grid_margin,
          isBundle(productName)
            ? classes.field_padding_oriental_bundle
            : classes.field_padding_oriental,
        ]}
      >
        {accNoListForOrientalAccount?.length > 0 &&
        customerType === 'ETB' &&
        isPlaidProduct(productName) ? (
          handleAccountsView()
        ) : (
          // <Grid  size={{ xs: 12 }}>
          <Grid size={{ xs: 12, md: gridSizeForBundle ? 12 : 6, lg: 6 }}>
            <CustomInput
              isBundle={isBundle(productName)}
              id="accountNumber"
              dataTestId="accountNumber_id"
              required
              inputLabelAriaLabel={`${t(isBundle(productName) ? ACCOUNT_NUM : ACCOUNT_NUMBER, {
                defaultValue: isBundle(productName) ? ACCOUNT_NUM : ACCOUNT_NUMBER,
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
              label={
                <span
                  style={{
                    color:
                      isBundle(productName) && fundDataErrors?.accountNumber ? 'red' : 'inherit',
                  }}
                >
                  {t(isBundle(productName) ? ACCOUNT_NUM : ACCOUNT_NUMBER, {
                    defaultValue: isBundle(productName) ? ACCOUNT_NUM : ACCOUNT_NUMBER,
                  })}
                </span>
              }
              handleChange={handleChange}
              placeholder={t(ENTER_ACCOUNT_NUMBER, {
                defaultValue: ENTER_ACCOUNT_NUMBER,
              })}
              fullWidth
              inputProps={
                // leadGenerationContext.productName === 'Progresa Account' ||
                // leadGenerationContext.productName === 'Cuenta Progresa' ||
                leadGenerationContext.productName === 'Cuenta Ahorra' ||
                leadGenerationContext.productName === 'Ahorra Account'
                  ? {
                      maxLength: 16,
                      'data-testid': 'account-number',
                      inputMode: 'numeric',
                    }
                  : {
                      maxLength: 12,
                      'data-testid': 'account-number',
                      inputMode: 'numeric',
                    }
              }
              value={commonUtility(fundData?.accountNumber)}
              error={commonUtility(fundDataErrors?.accountNumber)}
            />
            <Box
              component="span"
              sx={classes.error_message}
              role="alert"
              data-testid="account-number-error"
            >
              {t(fundDataErrors?.accountNumber, {
                defaultValue: fundDataErrors?.accountNumber,
              })}
            </Box>
          </Grid>
        )}
      </Grid>

      <Grid
        container
        size={{ md: gridSizeForBundle ? 12 : 6 }}
        sx={[
          classes.inputs_grid_margin,
          isBundle(productName)
            ? classes.field_padding_oriental_bundle
            : classes.field_padding_oriental,
        ]}
      >
        <Grid size={{ xs: 12 }} sx={classes.input_grid_padding}>
          <CustomInput
            isBundle={isBundle(productName)}
            dataTestId="depositAmount_id"
            id="depositAmount"
            startAdornment={
              isPlaidProduct(productName) ? null : (
                <InputAdornment position="start">$</InputAdornment>
              )
            }
            required={!isBundle(productName)}
            inputLabelAriaLabel={`${t(DEPOSIT_AMOUNT, {
              defaultValue: DEPOSIT_AMOUNT,
            })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
            autoFocus
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleFocus={handleFocus}
            label={
              <span
                style={{
                  color: isBundle(productName) && fundDataErrors?.depositAmount ? 'red' : 'inherit',
                }}
              >
                {t(isBundle(productName) ? 'DEPOSIT_AMOUNT_BUNDLE' : DEPOSIT_AMOUNT)}
              </span>
            }
            inputProps={{
              'data-testid': 'deposit-amount',
              maxLength: 7,
              inputMode: 'numeric',
            }}
            disabled={
              fundData?.depositAmount && (isCDproduct(productName) || isCDIraproduct(productName))
            }
            placeholder={
              isPlaidProduct(productName)
                ? `${t('Minimum deposit Prog', {
                    defaultValue: MINIMUM_DEPOSIT,
                  })} $${fundData?.minDepositAmount}.00`
                : `${t(MINIMUM_DEPOSIT, {
                    defaultValue: MINIMUM_DEPOSIT,
                  })}: $${fundData?.minDepositAmount}`
            }
            fullWidth
            error={commonUtility(fundDataErrors?.depositAmount)}
            value={commonUtilityValueReturn(fundData?.depositAmount, fundData?.depositAmount, '')}
          />

          <Box
            component="span"
            sx={classes.error_message}
            role="alert"
            data-testid="deposit-amount-error"
          >
            {fundDataErrors?.depositAmount === MIN_DEPOSIT_ERR && fundData?.minDepositAmount
              ? `${t(fundDataErrors?.depositAmount, {
                  defaultValue: fundDataErrors?.depositAmount,
                })}${fundData?.minDepositAmount}`
              : t(fundDataErrors?.depositAmount, {
                  defaultValue: fundDataErrors?.depositAmount,
                })}
          </Box>
        </Grid>
      </Grid>

      <Grid container size={{ md: gridSizeForBundle ? 12 : 6 }} sx={classes.inputs_grid_margin}>
        {(isCDproduct(productName) || isCDIraproduct(productName)) && (
          <Grid size={{ xs: 12 }} sx={classes.input_grid_padding}>
            <InputLabel
              htmlFor="sourceOfFunds"
              required
              id="sourceOfFunds_label"
              data-testid="sourceOfFunds_testid"
              aria-label={`${t(SOURCE_OF_FUNDS, { defaultValue: SOURCE_OF_FUNDS })} ${t(REQUIRED, {
                defaultValue: REQUIRED,
              })}`}
              sx={commonClass.label_title}
            >
              <span
                style={{
                  color:
                    isBundle(productName) && leadGenerationContext.fundDataErrors?.sourceofFunds
                      ? 'red'
                      : 'inherit',
                }}
              >
                {t(SOURCE_OF_FUNDS, { defaultValue: SOURCE_OF_FUNDS })}
              </span>
            </InputLabel>
            <Autocomplete
              id="sourceOfFunds"
              data-testid="autocomplete"
              name="sourceOfFunds"
              options={SourceOfFundsList}
              getOptionLabel={(option) => {
                if (!option || typeof option !== 'object') return '';
                return language === EN ? option.en || '' : option.es || '';
              }}
              value={leadGenerationContext.fundData.sourceofFunds}
              onChange={(_event, selectedValue) => {
                handleAutoCompleteFunds('sourceOfFunds', selectedValue);
              }}
              renderInput={(params) => (
                <OutlinedInput
                  id="sourceOfFunds"
                  name="sourceOfFunds"
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: '16px' },
                  }}
                  ref={params.InputProps.ref}
                  endAdornment={
                    isCDIraproduct(productName) && (
                      <Box padding="0 10px 0 0">
                        <DownArrowIcon
                          style={{ width: 24, height: 24 }}
                          color={theme?.palette?.orientalPrimary?.main}
                        />
                      </Box>
                    )
                  }
                  slotProps={{
                    notchedOutline: {
                      sx: !leadGenerationContext.fundDataErrors.sourceofFunds
                        ? commonClass.notchedOutline
                        : commonClass.notchedOutlineErr,
                    },
                    paper: {
                      sx: {
                        backgroundColor: theme?.menuBackground,
                        color: theme?.palette?.primary?.main,
                      },
                    },
                  }}
                  sx={[
                    commonClass.outlined_inputs,
                    {
                      '&::placeholder': {
                        fontSize: '16px',
                      },
                      paddingRight: '9px !important',
                    },
                    {
                      '&.Mui-focused': {
                        border: `1px solid ${theme?.palette?.primary?.main}`,
                      },
                    },
                  ]}
                  placeholder={t(SOURCE_OF_FUNDS_PLACEHOLDER, {
                    defaultValue: SOURCE_OF_FUNDS_PLACEHOLDER,
                  })}
                  fullWidth
                />
              )}
              filterOptions={filterOptionsEmp}
            />
            <Box
              component="span"
              role="alert"
              sx={styledClasses.error_message_product}
              data-testid="sourceOFFundsErr-id"
            >
              {t(leadGenerationContext.fundDataErrors.sourceofFunds, {
                defaultValue: leadGenerationContext.fundDataErrors.sourceofFunds,
              })}
            </Box>
          </Grid>
        )}
      </Grid>
      <Grid container size={{ md: gridSizeForBundle ? 12 : 6 }} sx={classes.inputs_grid_margin}>
        {!isPlaidProduct(productName) && (
          <Grid size={{ xs: 12 }} sx={classes.input_grid_padding}>
            <CustomSelect
              isBundle={isBundle(productName)}
              id="typeOfAccount"
              dataTestId="typeOfAccount_id"
              required
              inputLabelAriaLabel={`${t(isBundle(productName) ? ACCOUNT_TYPE : TYPE_OF_ACCOUNT, {
                defaultValue: isBundle(productName) ? ACCOUNT_TYPE : TYPE_OF_ACCOUNT,
              })}`}
              label={t(isBundle(productName) ? ACCOUNT_TYPE : TYPE_OF_ACCOUNT, {
                defaultValue: isBundle(productName) ? ACCOUNT_TYPE : TYPE_OF_ACCOUNT,
              })}
              options={['Checking', 'Savings']}
              nsSeparator="|"
              handleChange={handleChange}
              fullWidth
              value={commonUtility(fundData?.typeOfAccount)}
              selectAriaLabel={`${t('Aria Label Type Of Account', {
                defaultValue:
                  'Type Of Account Required Select Type Of Account dropdown menu button',
              })}`}
              error={fundDataErrors?.typeOfAccount}
            />
            <Box component="span" role="alert" sx={classes.error_message}>
              {t(fundDataErrors?.typeOfAccount, {
                defaultValue: fundDataErrors?.typeOfAccount,
              })}
            </Box>
          </Grid>
        )}
      </Grid>
      <Grid container size={{ md: gridSizeForBundle ? 12 : 6 }} sx={classes.inputs_grid_margin}>
        <Grid size={{ xs: 12 }} sx={classes.input_grid_padding}>
          {dgiMapping(leadGenerationContext.productName) ||
          dgiContMapping(leadGenerationContext.productName) ? (
            <CustomSelect
              required
              inputLabelAriaLabel={`${t(TAX_YEAR, {
                defaultValue: TAX_YEAR,
              })}`}
              dataTestId="taxYear_id"
              id="taxYear"
              options={['2024', '2025']}
              label={t(TAX_YEAR, { defaultValue: TAX_YEAR })}
              handleChange={handleChange}
              nsSeparator="|"
              value={commonUtility(fundData?.taxYear)}
              selectAriaLabel={`${t('Aria Label Tax Year', {
                defaultValue: 'Tax Year Required Select Tax Year dropdown menu button',
              })}`}
              fullWidth
              error={fundDataErrors?.taxYear}
            />
          ) : (
            ''
          )}
          <Box component="span" sx={classes.error_message} role="alert">
            {t(fundDataErrors?.taxYear, {
              defaultValue: fundDataErrors?.taxYear,
            })}
          </Box>
        </Grid>
      </Grid>
      {dgiContMapping(leadGenerationContext.productName) && (
        <Grid
          container
          size={{ md: gridSizeForBundle ? 12 : 6 }}
          sx={[classes.inputs_grid_margin, classes.field_padding_oriental]}
        >
          <Grid size={{ xs: 12 }} sx={classes.input_grid_padding}>
            <>
              <CustomInput
                dataTestId="dgiAccountno_id"
                id="dgiAccountNumber"
                required
                inputLabelAriaLabel={`${t(DGI_CONT_ACC_NUM, {
                  defaultValue: DGI_CONT_ACC_NUM,
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={t(DGI_CONT_ACC_NUM, { defaultValue: DGI_CONT_ACC_NUM })}
                handleChange={handleChangeDgiAccAndroid}
                placeholder="NEE/NEC-XXXXXX"
                inputProps={{ maxLength: 11 }}
                fullWidth
                error={commonUtility(fundDataErrors?.dgiAccountNumber)}
                value={commonUtility(fundData?.dgiAccountNumber)}
              />
              <Box
                component="span"
                role="alert"
                data-testid="account-number-error"
                sx={classes.error_message}
              >
                {t(fundDataErrors?.dgiAccountNumber, {
                  defaultValue: fundDataErrors?.dgiAccountNumber,
                })}
              </Box>
            </>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default OrientalAccount;

OrientalAccount.propTypes = {
  setIsSubmitClicked: PropTypes.func,
  isSubmitClicked: PropTypes.bool,
};
